/* eslint-disable max-lines */
import type { ProductCategory, ProductUpdate } from '../models/product-update.model';

/**
 * Mock product updates for development and testing
 * Covers all tabs: What's new, Coming soon, Timeline
 * Covers all product filters: All, Hire, Manage, Grow, AI
 */
export const MOCK_PRODUCT_UPDATES: ProductUpdate[] = [
  // ============================================
  // PLATFORM
  // ============================================
  {
    id: 'pu-platform-001',
    slug: 'centralized-settings',
    title: 'Centralized settings',
    summary:
      "We're consolidating system settings into a single, centralized location to make configuration faster and more intuitive.",
    content: `
      <h2>Streamlined Configuration Experience</h2>
      <p>Managing system settings across multiple areas of the platform can be time-consuming and confusing. We're addressing this challenge by consolidating all system settings into a single, centralized location.</p>

      <h3>What's Changing</h3>
      <p>Previously, administrators had to navigate to different sections of the platform to configure various settings. With this update, you'll find everything in one place, making it faster and more intuitive to manage your organization's configuration.</p>

      <h3>Key Benefits</h3>
      <ul>
        <li><strong>Single Source of Truth:</strong> All settings accessible from one location</li>
        <li><strong>Faster Configuration:</strong> Reduce time spent searching for specific settings</li>
        <li><strong>Improved Organization:</strong> Settings grouped logically by category</li>
        <li><strong>Better Visibility:</strong> See all your configurations at a glance</li>
      </ul>

      <h3>What to Expect</h3>
      <p>The new centralized settings panel will include user management, permissions, integrations, notifications, and company-wide preferences. We've designed the interface to be clean and easy to navigate, with search functionality to help you find exactly what you need.</p>

      <p>This update is part of our ongoing commitment to improving the administrator experience across the Tellent platform.</p>
    `,
    imageUrl: 'https://i.imgur.com/qu9ltES.jpeg',
    publishedAt: '2025-05-12T09:00:00Z',
    author: {
      name: 'Product Team',
      avatarUrl:
        'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=100&h=100&fit=crop&crop=face',
    },
    productCategories: ['Platform', 'Hire', 'Manage', 'Grow'],
    updateCategories: ['Feature'],
    isFeatured: true,
  },
  {
    id: 'pu-platform-002',
    slug: 'groups-and-departments',
    title: 'Groups and Departments',
    summary:
      "We're introducing Groups and Departments to help structure employee data more effectively. This will improve filtering, reporting, and permissions management by aligning the platform with your organizational setup.",
    content: `
      <h2>Better Organizational Structure</h2>
      <p>Understanding and managing your organization's structure is fundamental to effective HR operations. We're introducing Groups and Departments functionality to help you mirror your real-world organizational setup within the platform.</p>

      <h3>Why This Matters</h3>
      <p>Many organizations struggle with organizing employee data in ways that reflect their actual structure. This update allows you to create hierarchical groups and departments that match how your company actually operates.</p>

      <h3>Key Features</h3>
      <ul>
        <li><strong>Hierarchical Structure:</strong> Create nested departments and groups</li>
        <li><strong>Flexible Organization:</strong> Support for matrix and traditional structures</li>
        <li><strong>Enhanced Filtering:</strong> Filter employees, reports, and data by department</li>
        <li><strong>Permission Management:</strong> Assign access rights based on organizational structure</li>
        <li><strong>Improved Reporting:</strong> Generate reports segmented by department or group</li>
      </ul>

      <h3>Getting Started</h3>
      <p>Administrators can set up departments and groups through the new organizational settings panel. You can import existing structures or build them manually. The system supports unlimited nesting levels to accommodate complex organizational hierarchies.</p>

      <p>This feature integrates seamlessly with existing employee profiles, automatically updating as you make changes to your organizational structure.</p>
    `,
    imageUrl:
      'https://downloads.intercomcdn.com/i/o/q3qyzfii/1441782903/6d5a5d8941552ee0b9dab466bd17/AD_4nXfLbDmn7EDf10_k1PUTd5LFyJoNqc1-mGGVuOFeLwRXvC6R-TfI4xPHzG5D90-9BRM21bYtWgO9pNvIAaPeLkJbL2U_0Biwb2lLY6wseeGXq7lnxaznyCkIRoR61LJgUUSYtL_1?expires=1765784700&signature=15740a0a098243e61a4ea69eeaa7b943bcb455a515202f9a1e5371dd071865be&req=dSQjF852n4hfWvMW1HO4zeSJpVBc8H8OcW3yKOsousbIIVrItyQ7BGvgYXhx%0AfVvD%0A',
    publishedAt: '2025-03-01T09:00:00Z',
    author: {
      name: 'Sarah Chen',
      avatarUrl:
        'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face',
    },
    productCategories: ['Platform'],
    updateCategories: ['Feature'],
  },
  {
    id: 'pu-platform-003',
    slug: 'logo-customization',
    title: 'Logo customization',
    summary:
      "We'll enable organizations to personalize their account with custom logos. This update will strengthen internal branding and create a more familiar, company-aligned experience for users.",
    content: `
      <h2>Make Tellent Your Own</h2>
      <p>Brand identity matters, even in your internal tools. We're introducing logo customization to help your organization create a more personalized and familiar experience for all users.</p>

      <h3>Customization Options</h3>
      <p>With this update, you'll be able to upload your company logo and have it displayed throughout the platform. This includes the login screen, navigation header, email notifications, and exported documents.</p>

      <h3>Key Features</h3>
      <ul>
        <li><strong>Multiple Logo Formats:</strong> Support for light and dark mode variants</li>
        <li><strong>Flexible Placement:</strong> Logo appears in navigation, emails, and exports</li>
        <li><strong>Easy Upload:</strong> Simple drag-and-drop interface for logo management</li>
        <li><strong>Preview Mode:</strong> See how your logo looks before publishing changes</li>
      </ul>

      <h3>Benefits</h3>
      <p>Custom branding helps reinforce your company identity and makes the platform feel more integrated with your existing tools. Employees will see a consistent brand experience whether they're using Tellent or your other internal systems.</p>

      <p>This feature is especially valuable for organizations with strong brand guidelines or those who want to create a more cohesive employee experience across all touchpoints.</p>
    `,
    imageUrl:
      'https://downloads.intercomcdn.com/i/o/q3qyzfii/1441782889/b0ebb3c781637567048c27714b3f/AD_4nXdMLNxueHzdxFz401YFPrPS3twRRI0iF6mt17kwsPB3DOhLvR1m4y8x_pqlqhkcnlKlEsLe5Sr425vXRyweO-SWE5ekhW8DvQUIye8en-qL0ENm9XuEI-sUT0T9Oc_Ptz6hvkJAbQ?expires=1765784700&signature=107e7bf1a6395d364798f4d6d6e15fea558462c3420fc07ffc0293e7268f045e&req=dSQjF852n4lXUPMW1HO4zWOPkW%2FwNtitXPeIeiotIuc0H2uq8wrIbmlYYI5%2F%0AyJXz%0A',
    publishedAt: '2025-07-18T11:00:00Z',
    author: {
      name: 'Michael Torres',
      avatarUrl:
        'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face',
    },
    productCategories: ['Platform'],
    updateCategories: ['Feature'],
  },
  {
    id: 'pu-platform-004',
    slug: 'tellent-hr',
    title: 'Tellent HR',
    summary:
      'KiwiHR and Javelo are becoming united under one platform: Tellent HR – an all-in-one HRIS with integrated performance management. Current KiwiHR customers will be moved to Manage module (for core HR tasks), while also gaining ability to purchase the Grow module – designed to help manage feedback, development, and performance reviews, all in one place.',
    content: `
      <h2>Introducing Tellent HR</h2>
      <p>We're excited to announce the unification of KiwiHR and Javelo into a single, powerful platform: Tellent HR. This marks a significant milestone in our mission to provide organizations with comprehensive HR solutions.</p>

      <h3>What's Happening</h3>
      <p>Tellent HR combines the best of both worlds: KiwiHR's robust core HR functionality and Javelo's advanced performance management capabilities. This integration creates an all-in-one HRIS that covers the entire employee lifecycle.</p>

      <h3>For Current KiwiHR Customers</h3>
      <ul>
        <li><strong>Seamless Transition:</strong> Your existing features move to the Manage module</li>
        <li><strong>New Capabilities:</strong> Option to add the Grow module for performance management</li>
        <li><strong>Data Preservation:</strong> All your existing data and configurations are maintained</li>
        <li><strong>Improved Interface:</strong> Enhanced user experience with unified navigation</li>
      </ul>

      <h3>The Two Pillars</h3>
      <p><strong>Manage Module:</strong> Handles all core HR tasks including employee records, time-off management, document storage, and organizational structure.</p>
      <p><strong>Grow Module:</strong> Focuses on employee development with feedback tools, performance reviews, goal setting, and career development features.</p>

      <h3>Benefits of Unification</h3>
      <p>By bringing these platforms together, we eliminate data silos and create a seamless experience for HR teams and employees alike. You'll benefit from unified reporting, consistent user experience, and integrated workflows across all HR functions.</p>
    `,
    publishedAt: '2025-04-10T10:00:00Z',
    author: {
      name: 'Product Team',
      avatarUrl:
        'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=100&h=100&fit=crop&crop=face',
    },
    productCategories: ['Platform'],
    updateCategories: ['Feature'],
    youtubeUrl: 'https://www.youtube.com/watch?v=-iV2ySj_58Y',
    isFeatured: true,
  },
  {
    id: 'pu-platform-005',
    slug: 'one-canny',
    title: 'One Canny',
    summary:
      "We're consolidating our feedback channels into a single Canny board, making it easier for customers to submit ideas, vote on features, and track the status of their requests across all Tellent products.",
    content: `
      <h2>Unified Feedback Experience</h2>
      <p>Listening to our customers is essential to building the right features. We're consolidating our feedback management into a single Canny board to streamline how you share ideas and track feature requests.</p>

      <h3>What's Changing</h3>
      <p>Previously, different products had separate feedback channels, making it difficult to track requests and see the full picture. With One Canny, all feedback flows into a unified system.</p>

      <h3>Key Benefits</h3>
      <ul>
        <li><strong>Single Destination:</strong> One place to submit and track all feature requests</li>
        <li><strong>Cross-Product Voting:</strong> Vote on features across all Tellent products</li>
        <li><strong>Transparent Roadmap:</strong> See what's planned, in progress, and completed</li>
        <li><strong>Better Prioritization:</strong> Help us understand what matters most to you</li>
      </ul>

      <h3>How It Works</h3>
      <p>Submit your ideas through the feedback portal accessible from any Tellent product. Vote on existing suggestions to show your support. Track the status of requests as they move through our development process.</p>

      <p>Your voice matters in shaping the future of Tellent. We review all submissions and use voting data to inform our roadmap decisions.</p>
    `,
    publishedAt: '2025-06-10T09:00:00Z',
    author: {
      name: 'Emma Williams',
      avatarUrl:
        'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face',
    },
    productCategories: ['Platform'],
    updateCategories: ['Feature'],
  },
  {
    id: 'pu-platform-006',
    slug: 'one-billing-one-pricing',
    title: 'One Billing & One pricing',
    summary:
      "We're unifying billing and pricing across all Tellent products, providing customers with a single invoice, transparent pricing, and simplified subscription management for the entire platform.",
    content: `
      <h2>Simplified Billing Experience</h2>
      <p>Managing multiple subscriptions and invoices can be confusing and time-consuming. We're introducing unified billing and pricing to simplify your financial relationship with Tellent.</p>

      <h3>What's New</h3>
      <p>All Tellent products will now be billed through a single system with transparent, easy-to-understand pricing. No more juggling multiple invoices or trying to understand different pricing models.</p>

      <h3>Key Changes</h3>
      <ul>
        <li><strong>Single Invoice:</strong> One consolidated bill for all Tellent products</li>
        <li><strong>Unified Dashboard:</strong> Manage all subscriptions in one place</li>
        <li><strong>Transparent Pricing:</strong> Clear, consistent pricing across products</li>
        <li><strong>Flexible Plans:</strong> Mix and match modules to fit your needs</li>
        <li><strong>Easy Upgrades:</strong> Add new modules without complex negotiations</li>
      </ul>

      <h3>Benefits for Your Organization</h3>
      <p>Finance teams will appreciate the simplified invoicing, while administrators benefit from centralized subscription management. You'll have better visibility into your Tellent investment and easier budgeting for HR technology.</p>

      <p>Existing customers will be migrated to the new billing system with no action required on your part.</p>
    `,
    publishedAt: '2025-06-15T10:00:00Z',
    author: {
      name: 'Lisa Anderson',
      avatarUrl:
        'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=100&h=100&fit=crop&crop=face',
    },
    productCategories: ['Platform'],
    updateCategories: ['Feature'],
  },
  {
    id: 'pu-platform-007',
    slug: 'one-domain-for-tellent-hr',
    title: 'One domain (app.tellent.com)',
    summary:
      "We're consolidating all Tellent HR products under a single domain, providing users with a unified access point and simplified login experience across all modules.",
    content: `
      <h2>One Address for Everything</h2>
      <p>Remembering multiple URLs for different HR tools is unnecessary complexity. We're unifying all Tellent HR products under a single domain for a seamless access experience.</p>

      <h3>What's Changing</h3>
      <p>Instead of navigating to different domains for different products, you'll access everything through one unified address. This simplifies bookmarks, login management, and sharing links with colleagues.</p>

      <h3>Key Benefits</h3>
      <ul>
        <li><strong>Single URL:</strong> One address for all Tellent HR modules</li>
        <li><strong>Unified Login:</strong> Sign in once to access everything</li>
        <li><strong>Seamless Navigation:</strong> Move between modules without re-authenticating</li>
        <li><strong>Simplified Sharing:</strong> Share links that work for everyone in your organization</li>
      </ul>

      <h3>Migration Process</h3>
      <p>We'll handle the technical migration automatically. Your existing bookmarks will redirect to the new domain, and all your data remains exactly where it is. Users will be notified of the change with clear instructions.</p>

      <p>This consolidation is part of our broader effort to create a unified, cohesive Tellent HR experience.</p>
    `,
    publishedAt: '2025-11-01T11:00:00Z',
    author: {
      name: 'David Kim',
      avatarUrl:
        'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face',
    },
    productCategories: ['Platform'],
    updateCategories: ['Feature'],
  },
  {
    id: 'pu-platform-008',
    slug: 'grow-as-standalone-module',
    title: 'Grow as a standalone module',
    summary:
      "We're making the Grow module available as a standalone purchase, allowing organizations to access performance management and employee development tools without requiring the full Tellent HR suite.",
    content: `
      <h2>Flexible Performance Management</h2>
      <p>Not every organization needs a full HRIS, but everyone needs great performance management. We're making Grow available as a standalone module so you can access powerful development tools on their own.</p>

      <h3>Why Standalone?</h3>
      <p>Many companies already have core HR systems but lack effective performance management tools. By offering Grow independently, we meet organizations where they are in their HR technology journey.</p>

      <h3>What's Included</h3>
      <ul>
        <li><strong>Performance Reviews:</strong> Customizable review cycles and templates</li>
        <li><strong>Continuous Feedback:</strong> Real-time feedback and recognition tools</li>
        <li><strong>Goal Management:</strong> OKR and goal tracking capabilities</li>
        <li><strong>Development Plans:</strong> Career pathing and skill development</li>
        <li><strong>Analytics:</strong> Performance insights and reporting</li>
      </ul>

      <h3>Integration Options</h3>
      <p>Grow standalone integrates with popular HRIS platforms through our API and pre-built connectors. Sync employee data from your existing systems without duplicate data entry.</p>

      <p>Of course, you can always add other Tellent modules later as your needs evolve.</p>
    `,
    publishedAt: '2025-08-12T10:00:00Z',
    author: {
      name: 'Sophie Taylor',
      avatarUrl:
        'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=100&h=100&fit=crop&crop=face',
    },
    productCategories: ['Platform'],
    updateCategories: ['Feature'],
  },
  {
    id: 'pu-platform-009',
    slug: 'javelo-migration-to-grow-started',
    title: 'Javelo migration to Grow started',
    summary:
      "We've begun the migration of Javelo customers to the new Grow module, bringing enhanced performance management capabilities while preserving all existing data and configurations.",
    content: `
      <h2>Migration Underway</h2>
      <p>We're excited to announce that the migration of Javelo customers to the new Grow module has officially begun. This transition brings you enhanced capabilities while preserving everything you've built.</p>

      <h3>What to Expect</h3>
      <p>The migration process is designed to be seamless with minimal disruption to your operations. Our team is handling the technical details while keeping you informed every step of the way.</p>

      <h3>Migration Highlights</h3>
      <ul>
        <li><strong>Data Preservation:</strong> All historical reviews, feedback, and goals are migrated</li>
        <li><strong>Configuration Retention:</strong> Your custom templates and workflows are maintained</li>
        <li><strong>User Access:</strong> Team members retain their roles and permissions</li>
        <li><strong>Enhanced Features:</strong> Gain access to new Grow capabilities</li>
      </ul>

      <h3>Timeline</h3>
      <p>Migrations are being conducted in waves based on organization size and complexity. You'll receive direct communication with your specific timeline and any actions required.</p>

      <h3>Support</h3>
      <p>Our customer success team is available to answer questions and provide assistance throughout the migration process. We're committed to making this transition as smooth as possible.</p>
    `,
    publishedAt: '2025-09-08T09:00:00Z',
    author: {
      name: 'Product Team',
      avatarUrl:
        'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=100&h=100&fit=crop&crop=face',
    },
    productCategories: ['Platform'],
    updateCategories: ['Feature'],
  },

  // ============================================
  // HIRE
  // ============================================
  {
    id: 'pu-hire-001',
    slug: 'custom-fields-for-jobs-and-requisitions',
    title: 'Custom fields for Jobs and Requisitions',
    summary:
      "We're introducing customizable fields in job and requisition forms, enabling users to better align their hiring workflows with company policies. This will help streamline and standardize talent acquisition processes in Recruitee.",
    content: `
      <h2>Tailored Hiring Workflows</h2>
      <p>Every organization has unique hiring requirements. We're introducing custom fields for jobs and requisitions to help you capture the specific information that matters for your talent acquisition process.</p>

      <h3>Why Custom Fields?</h3>
      <p>Standard job fields don't always capture everything you need. Whether it's budget codes, hiring manager approvals, or department-specific requirements, custom fields let you build forms that match your actual workflows.</p>

      <h3>Key Capabilities</h3>
      <ul>
        <li><strong>Multiple Field Types:</strong> Text, numbers, dropdowns, dates, and more</li>
        <li><strong>Conditional Logic:</strong> Show fields based on previous answers</li>
        <li><strong>Required Fields:</strong> Ensure critical information is always captured</li>
        <li><strong>Template Support:</strong> Create reusable field configurations</li>
        <li><strong>Reporting Integration:</strong> Include custom fields in your reports</li>
      </ul>

      <h3>Use Cases</h3>
      <p>Common applications include tracking budget approvals, capturing compliance requirements, linking to internal systems, and standardizing hiring justifications. The flexibility means you can adapt the system to your specific needs.</p>

      <p>This feature supports better alignment between your hiring process and company policies, reducing manual work and improving consistency.</p>
    `,
    imageUrl:
      'https://downloads.intercomcdn.com/i/o/q3qyzfii/1327322970/06e92dc251a5872989a56c420c43/CleanShot+2025-01-09+at+10_31_26.png?expires=1765784700&signature=2765749b024c29598308d673ff91f556d6ca54ad5fea981c764f4b424a2035b2&req=dSMlEcp8n4hYWfMW1HO4zW5hGHN8SA9atd90QozHkepd7rZk%2F6Q%2FGW2XM8l2%0AkOex%0A',
    publishedAt: '2025-03-18T10:00:00Z',
    author: {
      name: 'Lisa Anderson',
      avatarUrl:
        'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=100&h=100&fit=crop&crop=face',
    },
    productCategories: ['Hire'],
    updateCategories: ['Feature'],
  },
  {
    id: 'pu-hire-002',
    slug: 'scheduling-rsvp',
    title: 'Scheduling RSVP',
    summary:
      "We're adding RSVP functionality to interview scheduling, allowing candidates to confirm, decline, or request alternative times directly from the invitation, streamlining the coordination process.",
    content: `
      <h2>Simplified Interview Coordination</h2>
      <p>Coordinating interview schedules often involves back-and-forth emails and manual tracking. Our new RSVP functionality streamlines this process by letting candidates respond directly to interview invitations.</p>

      <h3>How It Works</h3>
      <p>When you send an interview invitation, candidates receive a link to confirm their attendance, decline, or request an alternative time. Their response automatically updates in your system.</p>

      <h3>Key Features</h3>
      <ul>
        <li><strong>One-Click Responses:</strong> Candidates easily confirm or decline</li>
        <li><strong>Alternative Time Requests:</strong> Candidates can suggest different slots</li>
        <li><strong>Automatic Updates:</strong> Calendar and system updated instantly</li>
        <li><strong>Reminder Integration:</strong> Automated reminders for pending RSVPs</li>
        <li><strong>Status Dashboard:</strong> See all pending and confirmed interviews at a glance</li>
      </ul>

      <h3>Benefits</h3>
      <p>Recruiters save time by eliminating manual follow-ups, while candidates appreciate the easy response process. The result is faster scheduling, fewer no-shows, and a better experience for everyone involved.</p>

      <p>This feature integrates seamlessly with your existing calendar and notification settings.</p>
    `,
    publishedAt: '2025-02-20T09:00:00Z',
    author: {
      name: 'Marcus Johnson',
      avatarUrl:
        'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face',
    },
    productCategories: ['Hire'],
    updateCategories: ['Feature'],
  },
  {
    id: 'pu-hire-003',
    slug: 'merging-candidates-improvements',
    title: 'Merging candidates improvements',
    summary:
      "We're redesigning the candidate merging interface to make the process smoother and more intuitive. New options will give users greater control over the merging process, ensuring better usability and accuracy.",
    content: `
      <h2>Better Control Over Candidate Data</h2>
      <p>When candidates apply multiple times or through different channels, you need an easy way to consolidate their records. We've redesigned the candidate merging interface to make this process smoother and more accurate.</p>

      <h3>What's Improved</h3>
      <p>The new merging interface provides a side-by-side comparison of candidate records with clear visual indicators for conflicts and differences. You maintain full control over which data to keep.</p>

      <h3>New Features</h3>
      <ul>
        <li><strong>Side-by-Side Comparison:</strong> Easily see differences between records</li>
        <li><strong>Field-Level Control:</strong> Choose which values to keep for each field</li>
        <li><strong>Activity Consolidation:</strong> Merge application history intelligently</li>
        <li><strong>Undo Support:</strong> Reverse merges if needed within 24 hours</li>
        <li><strong>Duplicate Detection:</strong> Automatic suggestions for potential duplicates</li>
      </ul>

      <h3>Use Cases</h3>
      <p>This improvement is particularly valuable when candidates apply through multiple job boards, when referrals duplicate existing applicants, or when cleaning up historical data.</p>

      <p>The updated interface saves time while reducing the risk of data loss during the merging process.</p>
    `,
    publishedAt: '2025-02-12T11:00:00Z',
    author: {
      name: 'Sarah Chen',
      avatarUrl:
        'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face',
    },
    productCategories: ['Hire'],
    updateCategories: ['Feature'],
  },
  {
    id: 'pu-hire-004',
    slug: 'offer-letters',
    title: 'Offer letters',
    summary:
      "We'll introduce built-in offer letter functionality to streamline the final stage of hiring. HR teams will be able to generate, customize, and send offer letters directly from the platform—ensuring consistency, speed, and a smooth handoff into onboarding.",
    content: `
      <h2>Streamlined Offer Management</h2>
      <p>The offer stage is critical to closing great candidates. Our new built-in offer letter functionality helps you create professional, consistent offers and get them to candidates faster.</p>

      <h3>Complete Offer Workflow</h3>
      <p>Generate, customize, send, and track offer letters without leaving the platform. The entire process is integrated with your candidate pipeline and approval workflows.</p>

      <h3>Key Features</h3>
      <ul>
        <li><strong>Template Library:</strong> Pre-built templates for different roles and locations</li>
        <li><strong>Dynamic Fields:</strong> Auto-populate candidate and position details</li>
        <li><strong>Approval Workflows:</strong> Route offers for required approvals</li>
        <li><strong>E-Signature Integration:</strong> Candidates can sign digitally</li>
        <li><strong>Tracking Dashboard:</strong> Monitor offer status and acceptance rates</li>
        <li><strong>Onboarding Handoff:</strong> Seamless transition to onboarding workflows</li>
      </ul>

      <h3>Benefits</h3>
      <p>HR teams save hours on manual offer creation, while candidates receive professional, timely offers. Consistent templates ensure compliance and reduce errors. The integrated tracking helps you optimize your offer-to-acceptance rates.</p>

      <p>This feature marks a significant step toward end-to-end hiring management within Recruitee.</p>
    `,
    imageUrl:
      'https://downloads.intercomcdn.com/i/o/q3qyzfii/1441787246/4fe3ca15f6e6c0c7d92447257dd3/AD_4nXc74YEJOkbKA7LBNH1QbGHrxvnrJdqAYn5CNuaM0d_w4YQtFw01zLdlqsiS9xJw-bKWxb9a2jI4Tc9439o0KbxBzJZSQtoEsRESYGsC20LiYIwuE4AxpSksWR-8mCchV_pfRIu0eA?expires=1765784700&signature=fd0af4c9c7282e3746db05479521661608bc3b2e2884364089baca9f74d8b29d&req=dSQjF852moNbX%2FMW1HO4zTaItlnhRoJlVFc8DcLS1wtZLVxIz7cEjDZf0w4%2B%0AvBct%0A',
    publishedAt: '2025-07-22T10:00:00Z',
    author: {
      name: 'Rachel Green',
      avatarUrl:
        'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100&h=100&fit=crop&crop=face',
    },
    productCategories: ['Hire'],
    updateCategories: ['Feature'],
    isFeatured: true,
  },
  {
    id: 'pu-hire-005',
    slug: 'workflow-automations',
    title: 'Workflow automations',
    summary:
      "We're bringing more power to automation with conditional logic. You'll be able to set up automated actions that only trigger when specific criteria are met—enabling smarter workflows and reducing manual effort across the employee and recruitment lifecycle.",
    content: `
      <h2>Intelligent Automation</h2>
      <p>Repetitive tasks slow down your recruiting process. Our enhanced workflow automation with conditional logic lets you automate intelligently, triggering actions only when specific conditions are met.</p>

      <h3>Conditional Logic</h3>
      <p>Unlike simple automation that triggers on every event, conditional workflows evaluate criteria before acting. This means more precise automation that matches your actual processes.</p>

      <h3>Automation Capabilities</h3>
      <ul>
        <li><strong>If-Then Rules:</strong> Define conditions and corresponding actions</li>
        <li><strong>Multiple Triggers:</strong> Combine conditions with AND/OR logic</li>
        <li><strong>Delayed Actions:</strong> Schedule actions for future execution</li>
        <li><strong>Multi-Step Workflows:</strong> Chain actions in sequences</li>
        <li><strong>Custom Notifications:</strong> Send targeted alerts based on conditions</li>
      </ul>

      <h3>Example Use Cases</h3>
      <p>Automatically schedule technical interviews when candidates score above 80% on assessments. Send different rejection templates based on the stage reached. Notify specific team members based on the role being hired.</p>

      <p>This update significantly reduces manual work while ensuring consistency across your hiring process.</p>
    `,
    imageUrl:
      'https://downloads.intercomcdn.com/i/o/q3qyzfii/1441787234/c528de9700c415657b1ea90ec6eb/AD_4nXczO7PC5QRvNtdK_GOf5b4SRC8kX5aHC0cDC6SfZ-Wfbk1AKV4vipb6CevQS9EvycFKMqE4SYyTJZjFsMVoMA-810BPzCixPGTq2UJV1O6lBeUHLUGAtyyzAQSbOHrGRuygufOLKw?expires=1765784700&signature=f2096e1f5c549481a327b2f7031810b133b9fe267b8f4b2980ca349f87e13937&req=dSQjF852moNcXfMW1HO4zfYa3iWOh0sDGivACBDfqe6pqRSYaMAIpNHE6rj9%0AxTTM%0A',
    publishedAt: '2025-10-15T09:00:00Z',
    author: {
      name: 'Michael Torres',
      avatarUrl:
        'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face',
    },
    productCategories: ['Hire'],
    updateCategories: ['Feature'],
    isFeatured: true,
  },
  {
    id: 'pu-hire-006',
    slug: 'whatsapp-hiring',
    title: 'WhatsApp Hiring',
    summary:
      "We're integrating WhatsApp into your hiring workflow, allowing recruiters to communicate with candidates through their preferred messaging platform while keeping all conversations tracked in the ATS.",
    content: `
      <h2>Meet Candidates Where They Are</h2>
      <p>WhatsApp is the preferred communication channel for billions of people worldwide. We're bringing WhatsApp directly into your hiring workflow so you can engage candidates on their terms.</p>

      <h3>Integration Features</h3>
      <p>Send and receive WhatsApp messages without leaving Recruitee. All conversations are automatically logged to candidate profiles, maintaining a complete communication history.</p>

      <h3>Key Capabilities</h3>
      <ul>
        <li><strong>Two-Way Messaging:</strong> Send and receive messages in real-time</li>
        <li><strong>Template Messages:</strong> Use pre-approved templates for common scenarios</li>
        <li><strong>Media Support:</strong> Share documents, images, and files</li>
        <li><strong>Conversation History:</strong> All messages logged to candidate profiles</li>
        <li><strong>Team Visibility:</strong> Multiple team members can view conversations</li>
      </ul>

      <h3>Why WhatsApp?</h3>
      <p>WhatsApp messages have significantly higher open and response rates compared to email. For many candidates, especially in mobile-first regions, WhatsApp is the natural way to communicate professionally.</p>

      <p>This integration helps you improve candidate response rates while maintaining the tracking and compliance capabilities you need.</p>
    `,
    publishedAt: '2025-09-18T10:00:00Z',
    author: {
      name: 'Emma Williams',
      avatarUrl:
        'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face',
    },
    productCategories: ['Hire'],
    updateCategories: ['Feature'],
    youtubeUrl: 'https://www.youtube.com/watch?v=TICgXozd0vI',
    isFeatured: true,
  },
  {
    id: 'pu-hire-007',
    slug: 'career-sites-job-page-layouts-pages-library',
    title: '[Career sites] Job page layouts & Pages library',
    summary:
      "We're improving CareersHub setup with new tools that make it faster and easier to create professional-looking career pages. A library of pre-built pages will allow users to get started quickly, while multiple job page layouts will give more flexibility to tailor the candidate experience for specific roles.",
    content: `
      <h2>Beautiful Career Pages Made Easy</h2>
      <p>Your career site is often a candidate's first impression of your company. We're introducing new tools to help you create stunning career pages without design expertise.</p>

      <h3>Pages Library</h3>
      <p>Get started quickly with our library of pre-built page templates. Choose from designs optimized for different industries and company sizes, then customize to match your brand.</p>

      <h3>New Features</h3>
      <ul>
        <li><strong>Multiple Job Layouts:</strong> Different designs for different role types</li>
        <li><strong>Pre-Built Templates:</strong> Professional designs ready to customize</li>
        <li><strong>Drag-and-Drop Editor:</strong> Easy customization without coding</li>
        <li><strong>Mobile Optimization:</strong> All layouts look great on any device</li>
        <li><strong>Brand Customization:</strong> Match your company's visual identity</li>
      </ul>

      <h3>Tailored Candidate Experience</h3>
      <p>Use different layouts for different purposes: a vibrant design for creative roles, a professional layout for executive positions, or a detail-rich format for technical jobs.</p>

      <p>These improvements help you attract better candidates by showcasing your employer brand effectively.</p>
    `,
    imageUrl:
      'https://downloads.intercomcdn.com/i/o/q3qyzfii/1765378175/bb0be386f2463418caeeb22f9679/3016dfb1-338d-4fd2-8b75-9672d4461b0a?expires=1765784700&signature=2e28700a00d456784e2a403aa9285eda9f5e508194401c028c5f12bb699b4657&req=dSchE8p5lYBYXPMW1HO4zSFBejilV%2FL5xSimXLU1CcakncuMv5YNTfHggYOE%0A263O%0A',
    publishedAt: '2025-12-07T11:00:00Z',
    author: {
      name: 'Sophie Taylor',
      avatarUrl:
        'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=100&h=100&fit=crop&crop=face',
    },
    productCategories: ['Hire'],
    updateCategories: ['Feature'],
  },
  {
    id: 'pu-hire-008',
    slug: 'mobile-app-rebranding',
    title: 'MobileApp Rebranding',
    summary:
      "We're updating the Recruitee mobile app with fresh branding that aligns with the Tellent family, bringing a modern look and improved user experience to recruiting on the go.",
    content: `
      <h2>Fresh Look, Same Great Functionality</h2>
      <p>The Recruitee mobile app is getting a makeover. As part of the Tellent family, we're updating the app's branding while enhancing the overall user experience.</p>

      <h3>What's Changing</h3>
      <p>The updated app features the new Tellent visual identity with refreshed colors, typography, and icons. Beyond the visual refresh, we've also improved navigation and performance.</p>

      <h3>Updates Include</h3>
      <ul>
        <li><strong>New Branding:</strong> Tellent visual identity throughout the app</li>
        <li><strong>Improved Navigation:</strong> Easier access to key features</li>
        <li><strong>Performance Boost:</strong> Faster loading and smoother interactions</li>
        <li><strong>Dark Mode:</strong> Easier on the eyes in low light</li>
        <li><strong>Accessibility:</strong> Enhanced support for screen readers</li>
      </ul>

      <h3>Seamless Transition</h3>
      <p>The update will be delivered through your app store automatically. Your data and settings remain unchanged—you'll just enjoy a fresher, more modern experience.</p>

      <p>Continue recruiting on the go with the same powerful features in a polished new package.</p>
    `,
    publishedAt: '2025-03-08T09:00:00Z',
    author: {
      name: 'James Wilson',
      avatarUrl:
        'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop&crop=face',
    },
    productCategories: ['Hire'],
    updateCategories: ['Feature'],
  },
  {
    id: 'pu-hire-009',
    slug: 'new-job-reports-dashboard',
    title: 'New job reports dashboard',
    summary:
      "We're revamping the job reports dashboard to provide beginner users with clearer insights. The updated version will focus on showcasing key recruitment metrics and highlight the most important statistics for ongoing hiring efforts.",
    content: `
      <h2>Insights That Matter</h2>
      <p>Understanding your recruitment performance shouldn't require a data science degree. We're revamping the job reports dashboard to surface the insights that matter most, presented in a clear and actionable way.</p>

      <h3>Designed for Clarity</h3>
      <p>The new dashboard prioritizes the metrics that drive hiring decisions. Key statistics are front and center, with the ability to drill down into details when needed.</p>

      <h3>Dashboard Features</h3>
      <ul>
        <li><strong>At-a-Glance Metrics:</strong> See your most important KPIs immediately</li>
        <li><strong>Pipeline Visualization:</strong> Visual representation of candidate flow</li>
        <li><strong>Source Analysis:</strong> Understand where your best candidates come from</li>
        <li><strong>Time-to-Hire Tracking:</strong> Monitor hiring speed by role and team</li>
        <li><strong>Comparison Tools:</strong> Compare performance across periods</li>
      </ul>

      <h3>For All Users</h3>
      <p>Whether you're new to recruiting analytics or an experienced data user, the dashboard adapts to your needs. Simple views for quick insights, detailed views for deep analysis.</p>

      <p>This update helps everyone make better, data-informed hiring decisions.</p>
    `,
    imageUrl:
      'https://downloads.intercomcdn.com/i/o/q3qyzfii/1327346885/28549b3596ade7b28138474ea7b2/image.png?expires=1765784700&signature=e136a3823945fd99b7c22849da81038236cecc23a807e10a67d4b2122e76d596&req=dSMlEcp6m4lXXPMW1HO4zbR2amko1TutCbd36LGT51LsdL8LdReml0WIUBm2%0AXj24%0A',
    publishedAt: '2025-03-22T10:00:00Z',
    author: {
      name: 'David Kim',
      avatarUrl:
        'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face',
    },
    productCategories: ['Hire'],
    updateCategories: ['Feature'],
  },
  {
    id: 'pu-hire-010',
    slug: 'job-alerts',
    title: 'Job alerts',
    summary:
      'Candidates will soon be able to subscribe to notifications for new job postings on career pages, helping them stay updated on relevant opportunities with ease.',
    content: `
      <h2>Keep Candidates Engaged</h2>
      <p>Great candidates aren't always available when you have open roles. Job alerts help you stay connected with interested candidates by notifying them when relevant positions open up.</p>

      <h3>How It Works</h3>
      <p>Candidates visiting your career page can subscribe to job alerts based on their preferences. When matching positions are posted, they receive automatic notifications.</p>

      <h3>Alert Features</h3>
      <ul>
        <li><strong>Preference Settings:</strong> Candidates choose roles, locations, and departments</li>
        <li><strong>Instant Notifications:</strong> Alerts sent as soon as matching jobs are posted</li>
        <li><strong>Email & SMS:</strong> Multiple notification channels available</li>
        <li><strong>Easy Management:</strong> Candidates can update preferences anytime</li>
        <li><strong>Subscriber Analytics:</strong> See how many candidates are following your jobs</li>
      </ul>

      <h3>Benefits</h3>
      <p>Job alerts help you build a talent pipeline of interested candidates. When you post a new role, you immediately reach people who've already shown interest in your company.</p>

      <p>This feature improves candidate experience while giving you a head start on filling new positions.</p>
    `,
    imageUrl:
      'https://downloads.intercomcdn.com/i/o/q3qyzfii/1327325829/9a924f837d0b5cd6a08bcf2e1fd2/CleanShot+2025-01-09+at+10_33_56.png?expires=1765784700&signature=576ca2cfd55779c209e32ffa9460e3c0160366a4230e95ee4748b89ff1bd5db8&req=dSMlEcp8mIldUPMW1HO4zQ3eUJgQJXyyEsojEVQx0AnrGy7X%2FDGXJ7vDWO%2Ff%0AssXQ%0A',
    publishedAt: '2025-03-28T11:00:00Z',
    author: {
      name: 'Lisa Anderson',
      avatarUrl:
        'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=100&h=100&fit=crop&crop=face',
    },
    productCategories: ['Hire'],
    updateCategories: ['Feature'],
  },
  {
    id: 'pu-hire-011',
    slug: 'make-user-interface-more-joyful-to-use',
    title: 'Make User Interface (UI) more joyful to use',
    summary:
      "To enhance the user experience, we're updating our interface to make it more visually appealing. We've launched new Dashboards and are now focusing on improving text-heavy pages and reducing grey tones to add more vibrancy and joy to your workflows.",
    content: `
      <h2>A More Delightful Experience</h2>
      <p>Work tools don't have to be boring. We're refreshing the Recruitee interface to make it more visually appealing and enjoyable to use every day.</p>

      <h3>What's Changing</h3>
      <p>Building on our new dashboard designs, we're now updating text-heavy pages throughout the application. The goal is to reduce visual monotony while maintaining usability.</p>

      <h3>Design Improvements</h3>
      <ul>
        <li><strong>Vibrant Colors:</strong> Strategic use of color to reduce grey monotony</li>
        <li><strong>Improved Typography:</strong> Better readability and visual hierarchy</li>
        <li><strong>Whitespace Balance:</strong> More breathing room for content</li>
        <li><strong>Visual Feedback:</strong> Satisfying interactions and animations</li>
        <li><strong>Consistent Design:</strong> Unified look across all pages</li>
      </ul>

      <h3>Joy in the Details</h3>
      <p>Small touches make a big difference: subtle animations when completing actions, thoughtful empty states, and encouraging messages throughout your workflow.</p>

      <p>We believe that tools you enjoy using are tools you use more effectively. These updates aim to bring a little joy to your daily recruiting work.</p>
    `,
    imageUrl:
      'https://downloads.intercomcdn.com/i/o/q3qyzfii/1327362796/caa541272a0f4cf51845d7e3e393/CleanShot+2025-01-09+at+11_01_13.png?expires=1765784700&signature=d9af9badc590afaad22e74e14d57e7dd7716c9357a0a4fb8b87b781e5d9ae356&req=dSMlEcp4n4ZWX%2FMW1HO4zXLfrRusCcu8qJsio3U63GQ5UlGbKfnEqM%2BKHKQM%0A7CH%2B%0A',
    publishedAt: '2025-01-15T09:00:00Z',
    author: {
      name: 'Jennifer Martinez',
      avatarUrl:
        'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face',
    },
    productCategories: ['Hire'],
    updateCategories: ['Feature'],
  },

  // ============================================
  // MANAGE
  // ============================================
  {
    id: 'pu-manage-001',
    slug: 'silae-integration',
    title: 'Silae integration',
    summary:
      "We're introducing integration with Silae, a leading payroll solution in France, to streamline payroll processes. This enhancement will help ensure seamless data transfer and improve efficiency for HR teams managing payroll in supported regions.",
    content: `
      <h2>Streamlined French Payroll</h2>
      <p>For organizations operating in France, we're introducing integration with Silae, one of the country's leading payroll solutions. This connection eliminates manual data entry and ensures accurate payroll processing.</p>

      <h3>Integration Benefits</h3>
      <p>Employee data flows automatically between Tellent HR and Silae, reducing errors and saving time. Changes in either system are synchronized to maintain consistency.</p>

      <h3>Key Features</h3>
      <ul>
        <li><strong>Automatic Sync:</strong> Employee data synchronized in real-time</li>
        <li><strong>New Hire Transfer:</strong> Onboarded employees automatically added to payroll</li>
        <li><strong>Change Management:</strong> Updates flow between systems automatically</li>
        <li><strong>Termination Handling:</strong> Offboarded employees properly processed</li>
        <li><strong>Compliance Support:</strong> French payroll requirements handled correctly</li>
      </ul>

      <h3>Easy Setup</h3>
      <p>Connecting your Silae account takes just a few steps. Our support team is available to help with the initial configuration and data mapping.</p>

      <p>This integration is part of our commitment to supporting regional payroll requirements across Europe.</p>
    `,
    publishedAt: '2025-08-20T10:00:00Z',
    author: {
      name: 'Sarah Chen',
      avatarUrl:
        'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face',
    },
    productCategories: ['Manage'],
    updateCategories: ['Feature'],
  },
  {
    id: 'pu-manage-002',
    slug: 'journeys-pre-onboarding-reusable-steps',
    title: 'Journeys pre-onboarding & reusable steps',
    summary:
      'Our Journeys feature will soon support pre-onboarding, helping new hires transition seamlessly from offer acceptance to their start date. This update aims to reduce onboarding time and improve readiness for new employees. Additionally, enhanced template settings will make it easier to create and reuse steps for streamlined workflows.',
    content: `
      <h2>Start Before Day One</h2>
      <p>The period between offer acceptance and start date is valuable time. Our enhanced Journeys feature now supports pre-onboarding, helping new hires get ready before their first day.</p>

      <h3>Pre-Onboarding Capabilities</h3>
      <p>Engage new hires immediately after they accept. Send welcome materials, collect necessary documents, and complete paperwork before they walk through the door.</p>

      <h3>New Features</h3>
      <ul>
        <li><strong>Pre-Start Journeys:</strong> Automated workflows before day one</li>
        <li><strong>Document Collection:</strong> Gather required paperwork in advance</li>
        <li><strong>Welcome Content:</strong> Share company info and team introductions</li>
        <li><strong>Reusable Steps:</strong> Create step templates for common tasks</li>
        <li><strong>Step Library:</strong> Build a collection of reusable workflow components</li>
      </ul>

      <h3>Benefits</h3>
      <p>New hires arrive ready to be productive. HR teams spend less time on administrative tasks during the first week. The reusable steps feature means you build workflows once and use them across multiple journeys.</p>

      <p>This update transforms onboarding from a day-one event into a seamless experience that starts at offer acceptance.</p>
    `,
    imageUrl: 'https://i.imgur.com/0uqFDBI.jpeg',
    publishedAt: '2025-02-01T09:00:00Z',
    author: {
      name: 'Michael Torres',
      avatarUrl:
        'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face',
    },
    productCategories: ['Manage'],
    updateCategories: ['Feature'],
    isFeatured: true,
  },
  {
    id: 'pu-manage-003',
    slug: 'journeys-as-standalone-module',
    title: 'Journeys as a standalone module',
    summary:
      "We're making Journeys available as a standalone module, allowing organizations to implement powerful onboarding and employee transition workflows without requiring the full Manage suite.",
    content: `
      <h2>Focused Onboarding Solution</h2>
      <p>Great onboarding doesn't require a full HRIS. We're making Journeys available as a standalone module so you can implement powerful onboarding workflows independently.</p>

      <h3>Why Standalone?</h3>
      <p>Many organizations have existing HR systems but need better onboarding capabilities. Standalone Journeys lets you add sophisticated workflow automation without replacing your current tools.</p>

      <h3>Module Includes</h3>
      <ul>
        <li><strong>Workflow Builder:</strong> Create custom onboarding journeys</li>
        <li><strong>Task Management:</strong> Assign and track onboarding tasks</li>
        <li><strong>Document Collection:</strong> Gather required paperwork digitally</li>
        <li><strong>Progress Tracking:</strong> Monitor new hire onboarding status</li>
        <li><strong>Template Library:</strong> Pre-built workflows for common scenarios</li>
      </ul>

      <h3>Integration Options</h3>
      <p>Journeys standalone integrates with popular HR systems through our API. Import employee data from your HRIS and push completed information back as needed.</p>

      <p>Start improving your onboarding experience today, with the option to add more Tellent capabilities as your needs grow.</p>
    `,
    publishedAt: '2025-05-20T10:00:00Z',
    author: {
      name: 'Emma Williams',
      avatarUrl:
        'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face',
    },
    productCategories: ['Manage'],
    updateCategories: ['Feature'],
  },
  {
    id: 'pu-manage-004',
    slug: 'esignatures',
    title: 'eSignatures',
    summary:
      "We're integrating with DocuSign to provide a secure and reliable e-signature solution. This enhancement will also complement document generation based on templates, streamlining your workflows even further. More integrations to follow later.",
    content: `
      <h2>Sign Documents Digitally</h2>
      <p>Paper signatures slow everything down. We're introducing e-signature integration with DocuSign to help you get documents signed quickly and securely.</p>

      <h3>DocuSign Integration</h3>
      <p>Send documents for signature directly from Tellent HR. Track signing status and receive completed documents automatically. All signatures are legally binding and securely stored.</p>

      <h3>Key Features</h3>
      <ul>
        <li><strong>One-Click Sending:</strong> Send documents for signature with a single click</li>
        <li><strong>Template Integration:</strong> Combine with document generation for end-to-end automation</li>
        <li><strong>Status Tracking:</strong> Monitor who has signed and who hasn't</li>
        <li><strong>Automatic Storage:</strong> Signed documents saved to employee profiles</li>
        <li><strong>Reminders:</strong> Automated follow-ups for pending signatures</li>
      </ul>

      <h3>Common Use Cases</h3>
      <p>Employment contracts, policy acknowledgments, benefits enrollment forms, and performance review sign-offs all benefit from electronic signatures.</p>

      <p>Additional e-signature providers will be added in future updates based on regional requirements.</p>
    `,
    publishedAt: '2025-04-15T11:00:00Z',
    author: {
      name: 'Lisa Anderson',
      avatarUrl:
        'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=100&h=100&fit=crop&crop=face',
    },
    productCategories: ['Manage'],
    updateCategories: ['Feature'],
  },
  {
    id: 'pu-manage-005',
    slug: 'reminders-on-date-fields',
    title: 'Reminders on date fields',
    summary:
      "We're adding automated reminders for date fields to ensure no important deadlines are missed. From certification expirations to medical examination deadlines, this highly requested feature—especially popular in markets like France—will help teams stay informed and proactive about key employee data.",
    content: `
      <h2>Never Miss a Deadline</h2>
      <p>Important dates slip through the cracks when you're managing many employees. Our new automated reminders ensure you're always ahead of critical deadlines.</p>

      <h3>Automatic Alerts</h3>
      <p>Set up reminders for any date field in the system. Receive notifications days, weeks, or months in advance—whatever works for your process.</p>

      <h3>Common Use Cases</h3>
      <ul>
        <li><strong>Certification Expiration:</strong> Renew professional certifications on time</li>
        <li><strong>Medical Examinations:</strong> Schedule required health checks before deadlines</li>
        <li><strong>Work Permit Renewals:</strong> Stay compliant with immigration requirements</li>
        <li><strong>Probation Endings:</strong> Review employee performance at the right time</li>
        <li><strong>Contract Renewals:</strong> Don't miss fixed-term contract deadlines</li>
      </ul>

      <h3>Flexible Configuration</h3>
      <p>Configure reminder timing and recipients for each date type. Send alerts to HR, managers, or employees themselves. Set multiple reminder intervals for critical deadlines.</p>

      <p>This feature helps maintain compliance and reduces the risk of missed deadlines across your workforce.</p>
    `,
    publishedAt: '2025-04-22T09:00:00Z',
    author: {
      name: 'David Kim',
      avatarUrl:
        'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face',
    },
    productCategories: ['Manage'],
    updateCategories: ['Feature'],
  },
  {
    id: 'pu-manage-006',
    slug: 'customizable-employees-view',
    title: 'Customizable "Employees" view',
    summary:
      "We're making the employee list view more flexible by allowing users to customize visible columns and export the data. This will support easier reporting and better visibility into the information that matters most.",
    content: `
      <h2>Your Data, Your Way</h2>
      <p>Different teams need to see different employee information. We're making the employee list view fully customizable so you can see exactly what matters to you.</p>

      <h3>Customization Options</h3>
      <p>Choose which columns to display, reorder them to match your workflow, and save custom views for quick access. Different team members can have their own preferred layouts.</p>

      <h3>Key Features</h3>
      <ul>
        <li><strong>Column Selection:</strong> Show only the fields you need</li>
        <li><strong>Custom Ordering:</strong> Arrange columns in your preferred sequence</li>
        <li><strong>Saved Views:</strong> Store multiple view configurations</li>
        <li><strong>Quick Filters:</strong> Filter data directly from column headers</li>
        <li><strong>Export Capability:</strong> Download visible data to Excel or CSV</li>
      </ul>

      <h3>Use Cases</h3>
      <p>Create views for different purposes: a payroll view with compensation data, a compliance view with certification status, or a team view with manager and department information.</p>

      <p>This flexibility makes the employee list a powerful reporting tool, not just a directory.</p>
    `,
    publishedAt: '2025-06-12T10:00:00Z',
    author: {
      name: 'Sophie Taylor',
      avatarUrl:
        'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=100&h=100&fit=crop&crop=face',
    },
    productCategories: ['Manage'],
    updateCategories: ['Feature'],
  },
  {
    id: 'pu-manage-007',
    slug: 'documents-preview',
    title: 'Documents preview',
    summary:
      "We'll introduce in-platform document preview, allowing users to view documents without downloading them.",
    content: `
      <h2>View Without Downloading</h2>
      <p>Checking a document shouldn't require downloading it first. Our new in-platform document preview lets you view files instantly without leaving Tellent HR.</p>

      <h3>Preview Capabilities</h3>
      <p>View PDFs, images, and common document formats directly in your browser. Scroll through multi-page documents, zoom in on details, and navigate with ease.</p>

      <h3>Supported Formats</h3>
      <ul>
        <li><strong>PDF Documents:</strong> Contracts, policies, and reports</li>
        <li><strong>Images:</strong> ID photos, scanned documents, certificates</li>
        <li><strong>Office Documents:</strong> Word, Excel, and PowerPoint files</li>
        <li><strong>Text Files:</strong> Plain text and common text formats</li>
      </ul>

      <h3>Benefits</h3>
      <p>Save time by quickly reviewing documents without cluttering your downloads folder. Compare documents side by side. Access files securely without copying them to your device.</p>

      <p>This feature improves workflow efficiency while maintaining document security.</p>
    `,
    publishedAt: '2025-06-25T11:00:00Z',
    author: {
      name: 'Rachel Green',
      avatarUrl:
        'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100&h=100&fit=crop&crop=face',
    },
    productCategories: ['Manage'],
    updateCategories: ['Feature'],
  },
  {
    id: 'pu-manage-008',
    slug: 'journeys-api',
    title: 'Journeys API',
    summary:
      "We're unlocking new integration capabilities with the introduction of a robust API for the Journeys module. This will enable seamless data transfer from Journeys to external HR systems—eliminating manual work and supporting scalable pre-boarding workflows.",
    content: `
      <h2>Connect Journeys to Everything</h2>
      <p>The new Journeys API opens up powerful integration possibilities. Connect your onboarding workflows to external systems and automate data exchange.</p>

      <h3>API Capabilities</h3>
      <p>Programmatically create journeys, update progress, trigger actions, and extract data. Build custom integrations that match your exact requirements.</p>

      <h3>Key Endpoints</h3>
      <ul>
        <li><strong>Journey Management:</strong> Create, update, and manage journeys</li>
        <li><strong>Task Operations:</strong> Complete tasks and update status programmatically</li>
        <li><strong>Progress Tracking:</strong> Query journey progress and completion rates</li>
        <li><strong>Webhooks:</strong> Receive notifications when journey events occur</li>
        <li><strong>Data Export:</strong> Extract journey data for external analysis</li>
      </ul>

      <h3>Integration Examples</h3>
      <p>Trigger IT provisioning when new hire reaches certain step. Update your HRIS when onboarding completes. Sync task completion with project management tools.</p>

      <p>Comprehensive documentation and code examples make integration straightforward for your development team.</p>
    `,
    publishedAt: '2025-09-15T10:00:00Z',
    author: {
      name: 'Alex Rivera',
      avatarUrl:
        'https://images.unsplash.com/photo-1519345182560-3f2917c472ef?w=100&h=100&fit=crop&crop=face',
    },
    productCategories: ['Manage'],
    updateCategories: ['Feature'],
  },
  {
    id: 'pu-manage-010',
    slug: 'clock-in-station',
    title: 'Clock-in station',
    summary:
      "We're introducing a terminal-based clock-in solution to meet the high demand for time tracking across various roles, including production and warehouse employees. This feature will integrate seamlessly with time recording systems and support tracking and access control, ensuring accurate and efficient management of working hours.",
    content: `
      <h2>Simple, Reliable Time Tracking</h2>
      <p>Not all employees work at desks. Our new clock-in station provides a dedicated terminal solution for environments where mobile or desktop time tracking isn't practical.</p>

      <h3>Station Features</h3>
      <p>The clock-in station runs on dedicated devices positioned at entry points or work areas. Employees clock in and out with a simple tap or scan.</p>

      <h3>Key Capabilities</h3>
      <ul>
        <li><strong>Multiple Authentication:</strong> PIN, badge, or biometric options</li>
        <li><strong>Offline Mode:</strong> Continues working even without internet</li>
        <li><strong>Real-Time Sync:</strong> Data synchronized when connection available</li>
        <li><strong>Break Tracking:</strong> Log break times separately</li>
        <li><strong>Access Integration:</strong> Optional door access control</li>
      </ul>

      <h3>Ideal For</h3>
      <p>Manufacturing floors, warehouses, retail locations, healthcare facilities, and any environment with shift-based workers who need a quick, reliable way to record their hours.</p>

      <p>The station integrates with our broader time and attendance features for complete workforce time management.</p>
    `,
    imageUrl:
      'https://downloads.intercomcdn.com/i/o/q3qyzfii/1327312556/514ebddd134f0204b053c78ca614/CleanShot+2025-01-09+at+10_23_58.png?expires=1765784700&signature=79e398fdb8b7f920522c8724b3cb3423d4caf5e47c00aa1ff21e136049c2661f&req=dSMlEcp%2Fn4RaX%2FMW1HO4zTwV0dhaQpkK43rxUrXrtWdvg6zHXOSSnWIkEghn%0Ad59p%0A',
    publishedAt: '2025-03-15T10:00:00Z',
    author: {
      name: 'Marcus Johnson',
      avatarUrl:
        'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face',
    },
    productCategories: ['Manage'],
    updateCategories: ['Feature'],
  },
  {
    id: 'pu-manage-011',
    slug: 'bulk-documents-upload-and-assignment',
    title: 'Bulk documents upload and assignment',
    summary:
      "We're simplifying document management for HR teams by introducing bulk upload and assignment capabilities. This feature will make it easier to handle large volumes of employee-related documents—such as contracts, certifications, and compliance records.",
    content: `
      <h2>Document Management at Scale</h2>
      <p>Uploading documents one at a time is painfully slow. Our new bulk upload feature lets you process many documents at once and assign them to the right employees automatically.</p>

      <h3>Bulk Operations</h3>
      <p>Upload hundreds of documents in a single operation. The system can automatically match documents to employees based on naming conventions or embedded data.</p>

      <h3>Key Features</h3>
      <ul>
        <li><strong>Mass Upload:</strong> Drag and drop multiple files at once</li>
        <li><strong>Auto-Matching:</strong> Documents assigned based on employee identifiers</li>
        <li><strong>Category Assignment:</strong> Automatically categorize by document type</li>
        <li><strong>Validation:</strong> Check for completeness and correct assignment</li>
        <li><strong>Progress Tracking:</strong> Monitor upload and assignment status</li>
      </ul>

      <h3>Use Cases</h3>
      <p>Annual compliance documents, updated contracts, training certificates, or any situation where you need to distribute documents to many employees quickly.</p>

      <p>This feature dramatically reduces the time spent on document administration.</p>
    `,
    publishedAt: '2025-10-20T11:00:00Z',
    author: {
      name: 'Sarah Chen',
      avatarUrl:
        'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face',
    },
    productCategories: ['Manage'],
    updateCategories: ['Feature'],
  },
  {
    id: 'pu-manage-012',
    slug: 'tellent-hr-mobile-app-beta',
    title: 'Tellent HR Mobile App',
    summary:
      "We're launching a beta version of the Tellent HR mobile app, bringing core HR functionality to employees' smartphones for time-off requests, document access, and profile management on the go.",
    content: `
      <h2>HR in Your Pocket</h2>
      <p>Employees need HR access wherever they are. Our new mobile app brings essential Tellent HR functionality to iOS and Android devices.</p>

      <h3>Beta Features</h3>
      <p>The beta release focuses on the most requested mobile capabilities, with more features planned based on user feedback.</p>

      <h3>Available in Beta</h3>
      <ul>
        <li><strong>Time-Off Requests:</strong> Submit and check leave requests</li>
        <li><strong>Document Access:</strong> View personal documents and payslips</li>
        <li><strong>Profile Management:</strong> Update personal information</li>
        <li><strong>Team Directory:</strong> Find and contact colleagues</li>
        <li><strong>Notifications:</strong> Stay informed about important updates</li>
      </ul>

      <h3>Join the Beta</h3>
      <p>We're looking for organizations to participate in the beta program. Your feedback will directly shape the final release.</p>

      <p>The app complements the desktop experience, letting employees handle quick tasks without opening a laptop.</p>
    `,
    imageUrl: 'https://i.imgur.com/udoSTt3.jpeg',
    publishedAt: '2025-12-12T10:00:00Z',
    author: {
      name: 'James Wilson',
      avatarUrl:
        'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop&crop=face',
    },
    productCategories: ['Manage', 'Grow'],
    updateCategories: ['Feature'],
    isFeatured: true,
  },
  {
    id: 'pu-manage-013',
    slug: 'custom-fields-2-0',
    title: 'Custom fields 2.0',
    summary: 'New version of custom fields, with way more field types available.',
    content: `
      <h2>More Flexibility Than Ever</h2>
      <p>Custom fields are essential for capturing organization-specific data. Version 2.0 brings dramatically expanded field type options and improved usability.</p>

      <h3>New Field Types</h3>
      <p>Beyond basic text and numbers, you'll now have access to specialized field types that match real-world data requirements.</p>

      <h3>Available Field Types</h3>
      <ul>
        <li><strong>Rich Text:</strong> Formatted text with links and styling</li>
        <li><strong>File Attachments:</strong> Upload files directly to fields</li>
        <li><strong>Calculated Fields:</strong> Auto-compute values from other fields</li>
        <li><strong>Lookup Fields:</strong> Reference data from related records</li>
        <li><strong>Multi-Select:</strong> Choose multiple options from lists</li>
        <li><strong>Conditional Fields:</strong> Show/hide based on other values</li>
      </ul>

      <h3>Improved Management</h3>
      <p>The new custom fields interface makes it easier to create, organize, and manage your fields. Group related fields together and control visibility by user role.</p>

      <p>This update makes Tellent HR more adaptable to your unique data requirements.</p>
    `,
    publishedAt: '2026-01-25T09:00:00Z',
    author: {
      name: 'Product Team',
      avatarUrl:
        'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=100&h=100&fit=crop&crop=face',
    },
    productCategories: ['Manage'],
    updateCategories: ['Feature', 'Coming soon'],
  },

  // ============================================
  // GROW
  // ============================================
  {
    id: 'pu-grow-001',
    slug: 'app-hand-over-rewriting-started',
    title: 'App hand-over, rewriting started',
    summary:
      "We've begun the technical handover and rewriting of the Grow application, modernizing the codebase to enable faster feature development and improved performance.",
    content: `
      <h2>Building a Better Foundation</h2>
      <p>Great features require a solid foundation. We've begun a comprehensive rewrite of the Grow application to modernize the codebase and enable faster innovation.</p>

      <h3>Why Rewrite?</h3>
      <p>The existing Grow application has served well, but technology has evolved. A modernized architecture will let us deliver new features faster and improve the overall experience.</p>

      <h3>What's Happening</h3>
      <ul>
        <li><strong>Modern Stack:</strong> Updated technology for better performance</li>
        <li><strong>Improved Architecture:</strong> More maintainable and scalable design</li>
        <li><strong>Better Integration:</strong> Tighter connection with other Tellent modules</li>
        <li><strong>Enhanced UX:</strong> Opportunity to improve user experience</li>
        <li><strong>Future-Ready:</strong> Foundation for upcoming features</li>
      </ul>

      <h3>What It Means for You</h3>
      <p>During the transition, you'll continue using the current application without interruption. New features will gradually appear as parts of the rewrite are completed.</p>

      <p>This investment in our technical foundation will pay dividends in the features and improvements we can deliver going forward.</p>
    `,
    publishedAt: '2025-01-08T10:00:00Z',
    author: {
      name: 'Alex Rivera',
      avatarUrl:
        'https://images.unsplash.com/photo-1519345182560-3f2917c472ef?w=100&h=100&fit=crop&crop=face',
    },
    productCategories: ['Grow'],
    updateCategories: ['Feature'],
  },
  {
    id: 'pu-grow-002',
    slug: 'improved-employee-experience',
    title: 'Improved employee experience',
    summary:
      "We're creating a more visually appealing and modern interface for answering review questions, improving the immediate user experience while establishing reusable UI components for future enhancements.",
    content: `
      <h2>A Fresh Review Experience</h2>
      <p>Performance reviews are important moments for employees. We're refreshing the review interface to make it more engaging and easier to use.</p>

      <h3>What's Improving</h3>
      <p>The updated interface focuses on clarity and ease of use. Answering review questions feels more natural, and the visual design matches modern expectations.</p>

      <h3>Key Improvements</h3>
      <ul>
        <li><strong>Modern Design:</strong> Clean, contemporary visual style</li>
        <li><strong>Clear Progress:</strong> Always know where you are in the review</li>
        <li><strong>Better Forms:</strong> Easier input for all question types</li>
        <li><strong>Mobile Friendly:</strong> Complete reviews on any device</li>
        <li><strong>Autosave:</strong> Never lose your work in progress</li>
      </ul>

      <h3>Foundation for More</h3>
      <p>These improvements establish reusable components that will enhance other parts of Grow over time. The investment in better UI benefits the entire employee experience.</p>

      <p>We believe that tools people enjoy using lead to better, more thoughtful feedback.</p>
    `,
    publishedAt: '2025-04-18T11:00:00Z',
    author: {
      name: 'Sophie Taylor',
      avatarUrl:
        'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=100&h=100&fit=crop&crop=face',
    },
    productCategories: ['Grow'],
    updateCategories: ['Feature'],
  },
  {
    id: 'pu-grow-003',
    slug: 'flexible-reviews',
    title: 'Flexible reviews',
    summary:
      "To address challenges with rigid review processes, we're introducing a more flexible review creation system. This new approach will include four optional steps—nominations, individual opinions, summaries, and signatures—providing HR teams the freedom to design reviews that fit their needs. Over time, this update will replace Grow's current review offering and improve the overall experience.",
    content: `
      <h2>Reviews That Fit Your Process</h2>
      <p>Every organization approaches performance reviews differently. Our new flexible review system lets you design review processes that match how your company actually works.</p>

      <h3>Modular Approach</h3>
      <p>Instead of a rigid, one-size-fits-all structure, you can now compose reviews from optional building blocks. Use what you need, skip what you don't.</p>

      <h3>Available Components</h3>
      <ul>
        <li><strong>Nominations:</strong> Let employees or managers suggest reviewers</li>
        <li><strong>Individual Opinions:</strong> Collect feedback from multiple sources</li>
        <li><strong>Summaries:</strong> Consolidate feedback into actionable insights</li>
        <li><strong>Signatures:</strong> Formal acknowledgment of review completion</li>
      </ul>

      <h3>Design Your Reviews</h3>
      <p>Create light quarterly check-ins with just self-assessment. Build comprehensive annual reviews with 360 feedback. Design probation reviews with manager focus. The flexibility is yours.</p>

      <h3>Gradual Transition</h3>
      <p>This new system will eventually replace the current review offering, with plenty of time to transition your existing processes.</p>
    `,
    imageUrl: 'https://i.imgur.com/JMyNx4r.jpeg',
    publishedAt: '2025-11-12T10:00:00Z',
    author: {
      name: 'Emma Williams',
      avatarUrl:
        'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face',
    },
    productCategories: ['Grow'],
    updateCategories: ['Feature'],
    isFeatured: true,
  },
  {
    id: 'pu-grow-004',
    slug: 'skills-structure',
    title: 'Skills structure',
    summary:
      "We're laying the groundwork for a powerful, future-ready skills framework. This update will help organizations define, manage, and analyze skills across roles and seniority levels — setting the stage for smarter talent decisions.",
    content: `
      <h2>Building Your Skills Framework</h2>
      <p>Understanding your organization's skills is fundamental to talent management. We're introducing a structured approach to defining and tracking skills across your workforce.</p>

      <h3>Skills Framework</h3>
      <p>Create a comprehensive skills taxonomy tailored to your organization. Define skills, group them by category, and associate them with roles and levels.</p>

      <h3>Key Capabilities</h3>
      <ul>
        <li><strong>Skill Definitions:</strong> Create detailed skill descriptions</li>
        <li><strong>Proficiency Levels:</strong> Define what mastery looks like at each level</li>
        <li><strong>Role Mapping:</strong> Link skills to specific roles</li>
        <li><strong>Seniority Alignment:</strong> Set expected proficiency by level</li>
        <li><strong>Gap Analysis:</strong> Identify skill gaps across teams</li>
      </ul>

      <h3>Foundation for More</h3>
      <p>This skills structure is the foundation for future capabilities including career pathing, learning recommendations, and succession planning. Getting your skills framework right now enables smarter talent decisions later.</p>

      <p>Start building your skills library and see where your organization stands.</p>
    `,
    imageUrl:
      'https://downloads.intercomcdn.com/i/o/q3qyzfii/1589016871/c58686a95c19d4651138602c0916/CleanShot+2025-06-26+at+07_59_19.png?expires=1765784700&signature=83a4e5407fc622f3a09d6a1c4521065d9ab4e181525a369721747d0052fa1cbb&req=dSUvH8l%2Fm4lYWPMW1HO4zTpRdvAjEvTap7EmMJHJZ%2FTp2rUVJ0WhewHgp60y%0Ar4pd%0A',
    publishedAt: '2025-11-20T11:00:00Z',
    author: {
      name: 'David Kim',
      avatarUrl:
        'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face',
    },
    productCategories: ['Grow'],
    updateCategories: ['Feature'],
  },

  // ============================================
  // AI
  // ============================================
  {
    id: 'pu-ai-001',
    slug: 'ai-writer-rejection-emails',
    title: 'AI Writer: Rejection emails',
    summary:
      "Our upcoming AI-powered tool will help you create personalized rejection emails with ease. It will use candidate and role details to generate professional, empathetic messages aligned with your company's branding and tone. The tool will include legally-vetted templates, allow manual edits before sending, and ensure compliance with best practices.",
    content: `
      <h2>Thoughtful Rejections at Scale</h2>
      <p>Every candidate deserves a respectful response, even when the answer is no. Our AI Writer for rejection emails helps you send personalized, empathetic messages to every applicant.</p>

      <h3>How It Works</h3>
      <p>The AI analyzes candidate details and the role they applied for, then generates a personalized rejection email that maintains your company's tone and branding.</p>

      <h3>Key Features</h3>
      <ul>
        <li><strong>Personalization:</strong> References specific candidate details and role</li>
        <li><strong>Brand Alignment:</strong> Matches your company voice and style</li>
        <li><strong>Legal Compliance:</strong> Templates vetted for appropriate language</li>
        <li><strong>Edit Before Send:</strong> Review and customize every message</li>
        <li><strong>Batch Processing:</strong> Generate multiple emails efficiently</li>
      </ul>

      <h3>Why It Matters</h3>
      <p>Candidates remember how they were treated, even when not selected. A thoughtful rejection maintains your employer brand and keeps the door open for future opportunities.</p>

      <p>The AI handles the drafting so your team can focus on meaningful personalization.</p>
    `,
    publishedAt: '2025-03-10T09:00:00Z',
    author: {
      name: 'Sarah Chen',
      avatarUrl:
        'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face',
    },
    productCategories: ['AI'],
    updateCategories: ['Feature'],
  },
  {
    id: 'pu-ai-002',
    slug: 'ai-writer-evaluation-summaries',
    title: 'AI Writer: Evaluation summaries',
    summary:
      'The AI Writer will streamline candidate evaluation by synthesizing all written feedback into clear, concise summaries. It will highlight areas of agreement and disagreement among evaluators, extract key strengths and concerns, and maintain traceability to the original evaluations. The tool will allow quick verification and adjustments, saving time while ensuring accuracy.',
    content: `
      <h2>Faster, Better Hiring Decisions</h2>
      <p>Reading through multiple evaluations is time-consuming, and important details can get lost. Our AI Writer synthesizes feedback into actionable summaries that highlight what matters.</p>

      <h3>Intelligent Synthesis</h3>
      <p>The AI reads all evaluator feedback and creates a comprehensive summary that captures the full picture while remaining concise and actionable.</p>

      <h3>Summary Includes</h3>
      <ul>
        <li><strong>Key Strengths:</strong> What evaluators consistently praised</li>
        <li><strong>Areas of Concern:</strong> Potential issues raised by evaluators</li>
        <li><strong>Agreement Points:</strong> Where evaluators aligned</li>
        <li><strong>Disagreements:</strong> Areas with differing opinions</li>
        <li><strong>Source Links:</strong> Easy navigation to original feedback</li>
      </ul>

      <h3>Verification Made Easy</h3>
      <p>Every point in the summary links back to the original evaluation. Verify claims instantly and make adjustments before sharing with decision makers.</p>

      <p>This tool helps hiring committees make faster, more informed decisions.</p>
    `,
    publishedAt: '2025-03-11T09:00:00Z',
    author: {
      name: 'Michael Torres',
      avatarUrl:
        'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face',
    },
    productCategories: ['AI'],
    updateCategories: ['Feature'],
  },
  {
    id: 'pu-ai-003',
    slug: 'ai-writer-translate-jobs',
    title: 'AI Writer: Translate jobs',
    summary:
      'With just a click, the AI-powered tool will translate job postings into any language you need. This feature will help you reach a broader audience and support global recruitment efforts seamlessly.',
    content: `
      <h2>Reach Global Talent</h2>
      <p>Great candidates speak many languages. Our AI translation tool helps you post jobs in any language, expanding your reach to talent pools worldwide.</p>

      <h3>One-Click Translation</h3>
      <p>Select your target language and click translate. The AI produces natural, professional translations that read like they were written by a native speaker.</p>

      <h3>Translation Features</h3>
      <ul>
        <li><strong>50+ Languages:</strong> Major world languages supported</li>
        <li><strong>Professional Tone:</strong> Appropriate for job postings</li>
        <li><strong>Industry Terms:</strong> Handles technical vocabulary correctly</li>
        <li><strong>Review Option:</strong> Edit translations before publishing</li>
        <li><strong>Batch Translation:</strong> Translate multiple jobs at once</li>
      </ul>

      <h3>Global Recruitment</h3>
      <p>Whether you're expanding into new markets or recruiting for multilingual teams, translated job postings help you connect with candidates in their preferred language.</p>

      <p>Break down language barriers and find the best talent, wherever they are.</p>
    `,
    imageUrl:
      'https://downloads.intercomcdn.com/i/o/q3qyzfii/1327336057/a4ede1b28edd184d4e7b6961f42b/CleanShot+2025-01-09+at+10_41_13.png?expires=1765784700&signature=88aab2c154b991ee297b820f08e76e095baa269a96ba5855f9f6af4de3bcfb22&req=dSMlEcp9m4FaXvMW1HO4zUp9sN%2FfIyJyMFvrESWbd%2BN4c%2BgusWpo7hukKhZx%0ArU5B%0A',
    publishedAt: '2025-03-12T09:00:00Z',
    author: {
      name: 'Lisa Anderson',
      avatarUrl:
        'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=100&h=100&fit=crop&crop=face',
    },
    productCategories: ['AI'],
    updateCategories: ['Feature'],
  },
  {
    id: 'pu-ai-004',
    slug: 'ask-hr-ticketing-system',
    title: 'Ask HR & Ticketing system',
    summary:
      "We're introducing an AI-powered HR Assistant to support daily HR tasks. It will help answer employee questions, surface relevant information, and assist with common actions—saving time for HR teams and improving employee self-service.",
    content: `
      <h2>Your AI HR Assistant</h2>
      <p>Employees have questions, and HR teams are stretched thin. Our AI-powered HR Assistant provides instant answers while escalating complex issues to the right people.</p>

      <h3>AI-Powered Answers</h3>
      <p>Employees ask questions in natural language. The AI searches your policies, knowledge base, and employee data to provide accurate, instant responses.</p>

      <h3>Key Features</h3>
      <ul>
        <li><strong>Natural Language:</strong> Ask questions like you would a colleague</li>
        <li><strong>Policy Search:</strong> Instant answers from your documentation</li>
        <li><strong>Personal Data:</strong> Secure access to individual information</li>
        <li><strong>Ticket Creation:</strong> Automatic escalation when needed</li>
        <li><strong>Request Tracking:</strong> Monitor open and resolved tickets</li>
      </ul>

      <h3>Benefits for Everyone</h3>
      <p>Employees get instant answers to common questions. HR teams focus on complex issues that need human attention. Everyone saves time and gets better service.</p>

      <p>The ticketing system ensures nothing falls through the cracks when AI can't fully resolve an issue.</p>
    `,
    imageUrl: 'https://i.imgur.com/LdUzxIT.jpeg',
    publishedAt: '2025-09-12T10:00:00Z',
    author: {
      name: 'Product Team',
      avatarUrl:
        'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=100&h=100&fit=crop&crop=face',
    },
    productCategories: ['AI', 'Hire'],
    updateCategories: ['Feature'],
    isFeatured: true,
  },
  {
    id: 'pu-ai-005',
    slug: 'ai-insights-screening-assistant',
    title: 'AI Insights: Screening assistant',
    summary:
      "We're building an AI-powered assistant that helps recruiters and hiring managers screen candidates more effectively. The assistant will provide smart insights to accelerate shortlisting, reduce bias, and improve decision-making without replacing the human touch. This feature is designed to free up valuable recruiter time so they can focus on high-value interactions with candidates and hiring teams.",
    content: `
      <h2>Smarter Screening, Faster Hiring</h2>
      <p>Reviewing applications takes time. Our AI Screening Assistant analyzes candidates and provides insights that help you identify top talent faster while reducing unconscious bias.</p>

      <h3>How It Helps</h3>
      <p>The AI reviews applications against your job requirements and provides a structured analysis. It highlights relevant experience, identifies potential concerns, and suggests interview questions.</p>

      <h3>Key Capabilities</h3>
      <ul>
        <li><strong>Skills Matching:</strong> Compare candidate skills to requirements</li>
        <li><strong>Experience Analysis:</strong> Evaluate relevance of past roles</li>
        <li><strong>Question Suggestions:</strong> AI-generated interview questions</li>
        <li><strong>Bias Reduction:</strong> Focus on qualifications, not demographics</li>
        <li><strong>Batch Processing:</strong> Screen many applications quickly</li>
      </ul>

      <h3>Human in the Loop</h3>
      <p>The AI provides insights and recommendations, but humans make the final decisions. This approach combines AI efficiency with human judgment for the best outcomes.</p>

      <p>Spend less time on initial screening, more time on meaningful candidate conversations.</p>
    `,
    imageUrl:
      'https://downloads.intercomcdn.com/i/o/q3qyzfii/1762837331/3e102973562ffefc9ff85d29af1b/CleanShot+2025-10-06+at+09_50_38.png?expires=1765784700&signature=db3df6a474356cfb8c27422cb2769cd17355437824a045faf4590fab8cff47e3&req=dSchFMF9moJcWPMW1HO4zdERB91qXkuRyj4SQVO4Ck5GnrpezfiSknfhC4UH%0A3zcm%0A',
    publishedAt: '2026-01-01T10:00:00Z',
    author: {
      name: 'Sarah Chen',
      avatarUrl:
        'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face',
    },
    productCategories: ['AI'],
    updateCategories: ['Feature', 'Coming soon'],
    isFeatured: true,
  },
  {
    id: 'pu-grow-005',
    slug: 'company-skill-and-skill-gap-analysis',
    title: 'Company skill and skill gap analysis',
    summary:
      'Transform fragmented skill data into strategic workforce intelligence by delivering multi-dimensional analysis capabilities that enable data-driven talent decisions and proactive skill gap management across the entire organization.',
    content: `
      <h2>Strategic Workforce Intelligence</h2>
      <p>Understanding your organization's skill landscape is critical for making informed talent decisions. Our new skill gap analysis capabilities transform scattered skill data into actionable workforce intelligence.</p>

      <h3>Multi-Dimensional Analysis</h3>
      <p>Analyze skills across teams, departments, and the entire organization. Identify where skills are concentrated, where gaps exist, and how skill distribution evolves over time.</p>

      <h3>Key Capabilities</h3>
      <ul>
        <li><strong>Skill Inventory:</strong> Comprehensive view of all skills across the organization</li>
        <li><strong>Gap Identification:</strong> Automatically detect skill gaps against requirements</li>
        <li><strong>Trend Analysis:</strong> Track how skills develop and change over time</li>
        <li><strong>Team Comparison:</strong> Compare skill profiles across different teams</li>
        <li><strong>Strategic Planning:</strong> Align skill development with business objectives</li>
      </ul>

      <h3>Data-Driven Decisions</h3>
      <p>Move from gut-feel talent decisions to data-driven strategies. Understand which skills to hire for, which to develop internally, and where to focus learning investments.</p>

      <p>Proactively manage your workforce capabilities with comprehensive skill intelligence.</p>
    `,
    imageUrl: 'https://i.imgur.com/nwDwlXt.png',
    publishedAt: '2025-12-16T10:00:00Z',
    author: {
      name: 'Lisa Anderson',
      avatarUrl:
        'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=100&h=100&fit=crop&crop=face',
    },
    productCategories: ['Grow'],
    updateCategories: ['Feature'],
    isFeatured: true,
  },
  {
    id: 'pu-platform-010',
    slug: 'okta-scim',
    title: 'Okta SCIM',
    summary:
      'Integrate Tellent HR with Okta using SCIM (System for Cross-domain Identity Management) to automate user provisioning, deprovisioning, and synchronization across your identity management ecosystem.',
    content: `
      <h2>Seamless Identity Management</h2>
      <p>Managing user access across multiple systems is complex and error-prone. Our Okta SCIM integration automates the entire user lifecycle, ensuring consistent access management across your organization.</p>

      <h3>Automated Provisioning</h3>
      <p>When employees join, their accounts are automatically created in Tellent HR with the correct permissions based on their Okta profile. No manual setup required.</p>

      <h3>Key Features</h3>
      <ul>
        <li><strong>Auto-Provisioning:</strong> Create accounts automatically when users are added to Okta</li>
        <li><strong>Auto-Deprovisioning:</strong> Disable access immediately when employees leave</li>
        <li><strong>Attribute Sync:</strong> Keep user details synchronized between systems</li>
        <li><strong>Group Mapping:</strong> Map Okta groups to Tellent HR roles and permissions</li>
        <li><strong>Audit Trail:</strong> Complete visibility into all provisioning actions</li>
      </ul>

      <h3>Security & Compliance</h3>
      <p>Reduce security risks with instant deprovisioning. When someone leaves your organization in Okta, their Tellent HR access is automatically revoked—no delays, no forgotten accounts.</p>

      <p>Streamline identity management and strengthen your security posture with enterprise-grade SCIM integration.</p>
    `,
    publishedAt: '2026-01-22T10:00:00Z',
    author: {
      name: 'David Kim',
      avatarUrl:
        'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face',
    },
    productCategories: ['Platform'],
    updateCategories: ['Feature', 'Coming soon'],
  },
  {
    id: 'pu-manage-013',
    slug: 'smoother-journeys',
    title: 'Smoother Journeys',
    summary:
      'Optimize the process of setting up employee accounts that in the happy path leads to starting a new pre-onboarding or onboarding journey, creating a seamless experience from hire to first day.',
    content: `
      <h2>Seamless Employee Onboarding Flow</h2>
      <p>The transition from candidate to employee should be smooth and automatic. We're optimizing the entire flow from account creation to journey initiation, eliminating friction at every step.</p>

      <h3>Streamlined Account Setup</h3>
      <p>When a candidate is hired, their employee account is created with all the right information already in place. No duplicate data entry, no manual transfers between systems.</p>

      <h3>Key Improvements</h3>
      <ul>
        <li><strong>Auto-Population:</strong> Employee profiles pre-filled from candidate data</li>
        <li><strong>Smart Triggers:</strong> Journeys start automatically based on hire date</li>
        <li><strong>Validation Checks:</strong> Ensure all required information is complete before starting</li>
        <li><strong>Error Prevention:</strong> Catch issues early before they block the journey</li>
        <li><strong>Progress Visibility:</strong> Clear status of where each new hire is in the process</li>
      </ul>

      <h3>Happy Path Optimization</h3>
      <p>We've analyzed the most common onboarding scenarios and optimized for the happy path. For standard hires, the journey from offer acceptance to Day 1 is now almost entirely automated.</p>

      <p>Give every new hire a great first impression with a friction-free onboarding experience.</p>
    `,
    publishedAt: '2026-01-25T10:00:00Z',
    author: {
      name: 'Emma Williams',
      avatarUrl:
        'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face',
    },
    productCategories: ['Manage', 'Hire'],
    updateCategories: ['Feature', 'Coming soon'],
  },
];

/**
 * Get mock updates filtered by locale (returns same data for all locales in mock)
 */
export function getMockProductUpdates(locale: string = 'en'): ProductUpdate[] {
  return MOCK_PRODUCT_UPDATES;
}

/**
 * Get a single mock update by slug
 */
export function getMockProductUpdateBySlug(slug: string): ProductUpdate | undefined {
  return MOCK_PRODUCT_UPDATES.find(update => update.slug === slug);
}

/**
 * Get mock updates filtered by main tab (What's new, Coming soon, Timeline)
 */
export function getMockUpdatesByMainTab(
  tab: 'whats-new' | 'coming-soon' | 'changelog' | 'timeline',
): ProductUpdate[] {
  switch (tab) {
    case 'coming-soon':
      return MOCK_PRODUCT_UPDATES.filter(update => update.updateCategories.includes('Coming soon'));
    case 'changelog':
      return MOCK_PRODUCT_UPDATES.filter(update => update.updateCategories.includes('Changelog'));
    case 'timeline':
      // Timeline shows all updates sorted by date
      return [...MOCK_PRODUCT_UPDATES].sort(
        (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime(),
      );
    case 'whats-new':
    default:
      // What's new shows Feature, Improvement, New - but NOT Coming soon or Changelog-only posts
      return MOCK_PRODUCT_UPDATES.filter(
        update =>
          !update.updateCategories.includes('Coming soon') &&
          (update.updateCategories.includes('Feature') ||
            update.updateCategories.includes('Improvement') ||
            update.updateCategories.includes('New')),
      );
  }
}

/**
 * Get mock updates filtered by product category
 */
export function getMockUpdatesByProduct(
  product: 'all' | 'hire' | 'manage' | 'grow' | 'ai',
  updates: ProductUpdate[] = MOCK_PRODUCT_UPDATES,
): ProductUpdate[] {
  if (product === 'all') {
    return updates;
  }
  const productMap: Record<string, ProductCategory> = {
    hire: 'Hire',
    manage: 'Manage',
    grow: 'Grow',
    ai: 'AI',
  };
  return updates.filter(update => update.productCategories.includes(productMap[product]));
}
